/* Copyright (c) 2024 MOCA Systems, Inc. All rights reserved. */

import * as appStrings from '@touchplan/strings';
import { CommonModule } from '@angular/common';
import {
	ImportType,
	TicketImportErrorType,
	TicketImportFileUploadStatus,
	TicketImportOperationType,
	TicketImportSupportedFormats,
} from '@touchplan/common';
import { MaterialModule } from 'src/app/common/material/material-module';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { SharedUIMaterialModule } from 'src/app/shared/ui/material/shared-ui-material.module';
import { DialogReusableModule } from 'src/app/common/dialog-reusable/dialog-reusable.module';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { PresentationModule } from 'src/app/common/presentation-components/presentation.module';
import { IValidateResults, ImportTicketsStateService } from '../../services/import-tickets-state.service';
import { ImportTicketsService } from '../../services/import-tickets.service';
import { BehaviorSubject, Subject, combineLatest, map, take, takeUntil } from 'rxjs';
import { MatDialogModule } from '@angular/material/dialog';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import { UploadOutputFormat } from 'src/app/shared/ui/material/tp-file-upload.component/tp-file-upload.component';

@Component({
	selector: 'import-tickets-validation-results-step',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	templateUrl: './import-ticket-validation-results-step.component.html',
	styleUrls: ['./import-ticket-validation-results-step.component.scss'],
	imports: [
		CommonModule,
		MaterialModule,
		MatDialogModule,
		PresentationModule,
		ReactiveFormsModule,
		DialogReusableModule,
		SharedUIMaterialModule,
	],
})
export class ImportTicketValidationResultsStepComponent implements OnInit, OnDestroy {
	public formBuilder = inject(FormBuilder);
	private importTicketsService = inject(ImportTicketsService);
	private importTicketsState = inject(ImportTicketsStateService);

	private shutdownSubject$ = new Subject<void>();
	public appStrings = appStrings;
	public UploadOutputFormat = UploadOutputFormat;
	public ticketImportSupportedFormats = TicketImportSupportedFormats;
	public readonly lastError$ = this.importTicketsState.lastError$;
	public readonly issuesUrl$ = this.importTicketsState.issuesUrl$;
	public readonly validateImportTicketsData$ = combineLatest([this.lastError$, this.issuesUrl$]).pipe(
		takeUntil(this.shutdownSubject$),
		map(([lastError, issuesUrl]) => {
			return {
				lastError,
				issuesUrl,
			};
		})
	);
	public ticketImportFileUploadStatus = TicketImportFileUploadStatus;
	// Internal component state
	public ticketImportFileUploadStatus$: BehaviorSubject<TicketImportFileUploadStatus> = new BehaviorSubject(
		TicketImportFileUploadStatus.Waiting
	);

	protected TicketImportErrorType = TicketImportErrorType;

	get importFileFormControl() {
		return this.formGroup.get('importFile');
	}

	public formGroup = this.formBuilder.group({
		importFile: this.formBuilder.control<File>(null, [Validators.required]),
	});

	public onDownloadErrorWarningsFile(fileErrorUrl: string): void {
		this.importTicketsService.downloadErrorsFile(fileErrorUrl);
		this.importTicketsStateService
			.select((s) => s.importType)
			.pipe(take(1))
			.subscribe((importType) => {
				analyticsEventsService.importUpdateErrorsDownloaded(
					importType === ImportType.UPDATE || importType === ImportType.CREATE_AND_UPDATE
						? TicketImportOperationType.update
						: TicketImportOperationType.import
				);
			});
	}

	public onNext(): void {
		if (!this.formGroup.valid) {
			return;
		}
		this.importTicketsState.nextStep({
			...this.formGroup.value,
		} as IValidateResults);
	}

	public onBack(): void {
		this.formGroup.reset();
		this.importTicketsState.previousStep();
	}

	private loadRequiredData(): void {
		// importField is going to be set from the previous step as initial value
		this.importTicketsState.importFile$
			.pipe(take(1), takeUntil(this.shutdownSubject$))
			.subscribe((importFile) => {
				this.formGroup.patchValue({
					importFile,
				});
			});
	}

	private listenForFileChanges(): void {
		this.importFileFormControl.valueChanges.pipe(takeUntil(this.shutdownSubject$)).subscribe(() => {
			this.importTicketsState.updateValidationFileChange(true);
		});
	}

	ngOnInit(): void {
		this.loadRequiredData();
		this.listenForFileChanges();
	}

	ngOnDestroy(): void {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}

	constructor(private importTicketsStateService: ImportTicketsStateService) {}
}
